import { BUTTON_TYPES } from '../../../constants';
import {
  DecrementButton,
  IncrementButton,
  InputWithButtonsContainer,
  StyledNumberInput,
} from './styledComponents';
import { handleButtonClick, handleInputBlur } from '../utils';

import React, { useEffect, useState } from 'react';

/**
 * Component for number input with increment/decrement buttons
 * Used in both products and recipes columns
 */
export const NumberInputWithButtons = ({
  type,
  value,
  isDisabled,
  isForecastDisabled,
  isStockOrProducedDisabled,
  onValueChange,
  rowId,
  timeSlotId,
  timeSlotIndex,
  timeSlots,
  isFocused,
  onFocusChange,
  sortedVisibleRows,
  createKeyDownHandler,
  allowDecimals = false,
}) => {
  const [inputValue, setInputValue] = useState(value === null ? '' : value);

  // Update local state when value prop changes (e.g., after reset)
  useEffect(() => {
    setInputValue(value === null ? '' : value);
  }, [value]);

  const handleKeyDown = createKeyDownHandler({
    type,
    rowId,
    timeSlotId,
    timeSlotIndex,
    timeSlots,
    onFocusChange,
    sortedVisibleRows,
    isForecastDisabled,
    isStockOrProducedDisabled,
  });

  return (
    <InputWithButtonsContainer data-input-container>
      {isFocused && !isDisabled && (
        <DecrementButton
          disabled={inputValue === null || inputValue === 0}
          data-decrement-button
          onMouseDown={(e) => {
            if (inputValue === null || inputValue === 0) {
              return;
            }

            handleButtonClick(e, BUTTON_TYPES.DECREMENT, setInputValue);
          }}
        >
          −
        </DecrementButton>
      )}

      <StyledNumberInput
        disabled={isDisabled}
        inputProps={{
          min: 0,
          'data-focus-id': `${rowId}-${timeSlotId}-${type}`,
          style: {
            textAlign: 'center',
            fontSize: '12px',
            padding: '0',
            height: '100%',
          },
        }}
        key={`${rowId}-${timeSlotId}-${type}`}
        placeholder="-"
        size="small"
        type="number"
        value={inputValue}
        variant="standard"
        onBlur={(e) => {
          // The displayed value can be rounded (e.g. recipe stock in batches): saving it untouched would alter the stored value
          if (e.target.value === e.target.dataset.originalValue) {
            return;
          }

          handleInputBlur(e.target.value, onValueChange, rowId, timeSlotId, allowDecimals);
        }}
        onChange={(e) => setInputValue(e.target.value)}
        onFocus={(e) => {
          // Store the original value in a data attribute
          e.target.dataset.originalValue = e.target.value;

          // Select the input value
          e.target.select();

          onFocusChange(`${rowId}-${timeSlotId}-${type}`);
        }}
        onKeyDown={(e) => {
          // Cancelling displays the saved value again, so the blur that follows saves nothing
          if (e.key === 'Escape') {
            setInputValue(value === null ? '' : value);
          }

          handleKeyDown(e);
        }}
      />

      {isFocused && !isDisabled && (
        <IncrementButton
          data-increment-button
          onMouseDown={(e) => handleButtonClick(e, BUTTON_TYPES.INCREMENT, setInputValue)}
        >
          +
        </IncrementButton>
      )}
    </InputWithButtonsContainer>
  );
};
